import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IsMobile } from '../utils/Mobile';

// How far the tip sits from the cursor, and from the window's edges
const Offset = 14;
const Edge = 4;

// A part of an entry that another card adds through it, under it: "+2 from Antoni Gaudí" (included in the entry's
// number, not on top of it); all of it from that card (the card itself makes none, e.g. Coal's science from Marie
// Curie), or a part shown by name only (Jan Žižka's farms), just the card's name
const FromText = (entry, part) => {
    const own = entry.value - (entry.from ?? []).filter(other => !other.unnumbered).reduce((sum, other) => sum + other.value, 0);
    if (part.unnumbered || own === 0)
    {
        return part.source;
    }
    return `${part.value > 0 ? '+' : '−'}${Math.abs(part.value)} from ${part.source}`;
};

// Whether what the mouse is over (inside the hovered element) has hover text of its own, e.g. a pool beside a counter:
// that is shown instead
const OwnTitle = (element, wrapper) => {
    const titled = element.closest('[title]');
    return !!titled && titled !== wrapper && wrapper.contains(titled) && titled.title !== '';
};

// Where a total comes from, on hover, as a small table: each source and its value (what part of it comes from what,
// under it). label: a heading, e.g. "Military strength"; omitted when what is hovered says it already.
// As: the element wrapping what is hovered (a div, or a span inside a line).
// The tip follows the cursor, below and to its right, or above / to its left where the window ends.
function BreakdownTip({breakdown, label, As = 'div', className, style, children})
{
    const target = useRef(null);
    const tip = useRef(null);
    const [cursor, setCursor] = useState(null);
    const [position, setPosition] = useState(null);
    const follow = (e) => setCursor(OwnTitle(e.target, target.current) ? null : { x: e.clientX, y: e.clientY });
    // (on a phone, which has no hover: a tap shows it where it was tapped, and the next tap anywhere closes it)
    useEffect(() => {
        if (!IsMobile || !cursor)
        {
            return undefined;
        }
        const close = () => setCursor(null);
        document.addEventListener('pointerdown', close);
        return () => document.removeEventListener('pointerdown', close);
    }, [cursor]);
    useLayoutEffect(() => {
        if (!cursor || !tip.current)
        {
            setPosition(null);
            return;
        }
        const { width, height } = tip.current.getBoundingClientRect();
        const left = cursor.x + Offset + width > window.innerWidth - Edge ? cursor.x - Offset - width : cursor.x + Offset;
        const top = cursor.y + Offset + height > window.innerHeight - Edge ? cursor.y - Offset - height : cursor.y + Offset;
        setPosition({ left: Math.max(Edge, left), top: Math.max(Edge, top) });
    }, [cursor]);
    return (
        <>
            <As ref={target} className={className} style={style}
                {...(IsMobile ? { onClick: (e) => setCursor({ x: e.clientX, y: e.clientY }) }
                    : { onMouseOver: follow, onMouseMove: follow, onMouseLeave: () => setCursor(null) })}>
                {children}
            </As>
            {cursor && createPortal(
                // (placed once measured, so it never shows past the window's edge)
                <div ref={tip} className="tta-breakdown-tip" style={{ ...position, visibility: position ? 'visible' : 'hidden' }}>
                    {label && <div className="tta-breakdown-tip-label">{label}</div>}
                    {breakdown.length === 0
                        ? <div className="tta-breakdown-tip-none">Nothing contributes yet</div>
                        : <table><tbody>
                            {breakdown.map((entry, index) => [
                                <tr key={index}><td>{entry.source}{entry.note && <span className="tta-breakdown-tip-note"> {entry.note}</span>}</td><td className="tta-breakdown-tip-value">{entry.value}</td></tr>,
                                ...(entry.from ?? []).map((part, partIndex) =>
                                    <tr key={`${index}-${partIndex}`} className="tta-breakdown-tip-from">
                                        <td colSpan={2}>{FromText(entry, part)}</td></tr>),
                            ])}
                        </tbody></table>}
                </div>, document.body)}
        </>
    );
}

export default BreakdownTip;
